import { z } from 'zod';
import { isValid, isBefore, startOfDay, isAfter } from 'date-fns';

import { EventType, EventFormat, T2DomainEnum } from 'types/event.types';
import { convertDateToUtc } from 'utils/dateTimeUtils';
import { createT2DomainRefinement } from 'pages/dashboard/overview/events/schemas/commonUtils';

// Get user's timezone using native browser API
const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const duplicateEventObject = z.object({
  duplicateType: z.enum([EventType.SINGLE, EventType.MULTI_SESSIONS]).default(EventType.SINGLE),
  startDateTime: z
    .date()
    .optional()
    .refine((date) => !date || isValid(date), {
      message: 'Invalid start date and time'
    }),
  endDateTime: z
    .date()
    .optional()
    .refine((date) => !date || isValid(date), {
      message: 'Invalid end date and time'
    }),
  allDay: z.boolean().default(false),
  timezoneCode: z.string().optional().default(getUserTimezone()),
  connectSlackChannel: z.boolean().nullable().optional(),
  title: z.string().optional(),
  format: z.nativeEnum(EventFormat).optional(),
  virtualLink: z.string().optional().or(z.literal('')),
  inPersonLocation: z.string().optional().or(z.literal('')),
  zoomMeetingId: z.number().int().optional().nullable(),
  googleMeetingId: z.number().int().optional().nullable(),
  t2DomainArray: z.array(z.nativeEnum(T2DomainEnum)).optional().nullable()
});

interface SchemaFactoryOptions {
  t2DomainEnabled?: boolean;
}

export const getDuplicateEventSchema = (
  eventType: EventType,
  options: SchemaFactoryOptions
): z.ZodType<any> => {
  const { t2DomainEnabled } = options;

  return duplicateEventObject
    .superRefine(createT2DomainRefinement(!!t2DomainEnabled))
    .superRefine((data, ctx) => {
      const sourceEventType = eventType;
      const selectedTimezone = data.timezoneCode || getUserTimezone();

      const nowUtc = convertDateToUtc(
        data.allDay || data.duplicateType === EventType.MULTI_SESSIONS
          ? startOfDay(new Date())
          : new Date(),
        selectedTimezone
      );

      // Validate start date is in future if provided
      if (data.startDateTime) {
        const startDateUtc = convertDateToUtc(data.startDateTime, selectedTimezone);

        if (isBefore(startDateUtc, nowUtc)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Start date must be in the future',
            path: ['startDateTime']
          });
        }
      }

      // Validate end date is in future if provided
      if (data.endDateTime) {
        const endDateUtc = convertDateToUtc(data.endDateTime, selectedTimezone);

        if (isBefore(endDateUtc, nowUtc)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'End date must be in the future',
            path: ['endDateTime']
          });
        }
      }

      // Validate dates relationship if both are provided
      if (data.startDateTime && data.endDateTime) {
        // Convert both dates to UTC using the selected timezone for proper comparison
        const startDateUtc = convertDateToUtc(data.startDateTime, selectedTimezone);
        const endDateUtc = convertDateToUtc(data.endDateTime, selectedTimezone);

        // Check if end date is before start date
        if (
          (data.allDay && isBefore(endDateUtc, startDateUtc)) ||
          (!data.allDay && !isAfter(endDateUtc, startDateUtc))
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'End date must be after start date',
            path: ['endDateTime']
          });
        }
      }

      // Validation rules based on duplicate type
      if (data.duplicateType === EventType.SINGLE) {
        if (!data.startDateTime) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Start time is required',
            path: ['startDateTime']
          });
        }
        if (!data.endDateTime) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'End time is required',
            path: ['endDateTime']
          });
        }

        // Require format when duplicating multi-session as single
        if (sourceEventType === EventType.MULTI_SESSIONS && !data.format) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Format is required',
            path: ['format']
          });
        }
      } else if (
        data.duplicateType === EventType.MULTI_SESSIONS &&
        sourceEventType === EventType.MULTI_SESSIONS
      ) {
        if (!data.startDateTime) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Start time is required',
            path: ['startDateTime']
          });
        }
      }
    });
};

export type DuplicateEventFormValues = z.infer<ReturnType<typeof getDuplicateEventSchema>>;
